import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, ArrowLeft } from "lucide-react";
import { showToast } from "../utils/toast";
import FormField from "../components/FormField";
import { validateEmail, validateOTP } from "../utils/formValidation";
import banner_v2 from "./banner_v2.mp4";
import RotatingText from "../components/Typewriter";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authStep, setAuthStep] = useState("email");
  const [otpTimer, setOtpTimer] = useState(0);
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const videoRef = useRef(null);

  const { login, verifyOTP, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
        return;
      }

      switch (user.role) {
        case "admin": navigate("/admin/dashboard"); break;
        case "business_head": navigate("/business-head/dashboard"); break;
        case "employee": 
        case "vendor": navigate("/employee/dashboard"); break;
        default: break;
      }
    }
  }, [user, navigate]);

  const handleOtpChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(v);
  };

  const sendOTP = async () => {
    const emailVal = validateEmail(email);
    if (!emailVal.isValid) {
      showToast.error(emailVal.message);
      return;
    }

    try {
      setIsLoading(true);
      setIsEmailLocked(true);
      setError("");
      await login(email);
      showToast.success("OTP sent to your email");
      setAuthStep("otp");
      setOtpTimer(60);
    } catch (err) {
      showToast.error(err.message);
      setError(err.message);
      setIsEmailLocked(false);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTPCode = async () => {
    const otpVal = validateOTP(otp);
    if (!otpVal.isValid) {
      showToast.error(otpVal.message);
      return;
    }

    try {
      setIsLoading(true);
      const resp = await verifyOTP(email, otp);

      if (resp?.user) {
        showToast.success(`Welcome ${resp.user.full_name || ""}`);

        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
          return;
        }

        if (resp.user.role === "admin") navigate("/admin/dashboard");
        if (resp.user.role === "business_head") navigate("/business-head/dashboard");
        if (resp.user.role === "employee" || resp.user.role === "vendor") navigate("/employee/dashboard");
      }
    } catch (err) {
      setOtp("");
      showToast.error(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setAuthStep("email");
    setOtp("");
    setError("");
    setIsEmailLocked(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  if (isLoading) return;

  if (authStep === "email") {
    sendOTP();
  } else {
    verifyOTPCode();
  }
};

  useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpTimer]);

  

  return (
    <div className="min-h-screen w-full flex relative">

      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        disablePictureInPicture
        controls={false}
        onCanPlay={() => {
          try {
            videoRef.current && videoRef.current.play();
          } catch (e) { }
        }}
        className="absolute top-0 left-0 w-full h-full object-cover"
        style={{ pointerEvents: 'none' }}
      >
        <source src={banner_v2} type="video/mp4" />
      </video>

      {/* Dark Overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-black/50"></div>

      {/* Center Panel */}
      <div className="flex flex-col z-20 justify-center items-center w-full px-8">

        <Card className="w-full max-w-md bg-black/30 backdrop-blur-2xl shadow-2xl rounded-2xl border border-white/10 text-white">

          <CardHeader className="text-center space-y-1">
            <div className="mb-10 relative">
              <img
                src="./Mosil.png"
                alt="Mosil Logo"
                className="h-12 absolute left-1/2 -translate-x-1/2 -bottom-10 object-contain"
              />
            </div>

            <div className="mb-4">
              <RotatingText />
            </div>

            <CardTitle className="text-xl sm:text-3xl font-bold text-white">Log In</CardTitle>
            <CardDescription className="text-gray-300 font-medium">
              {authStep === "email"
                ? "Enter your email to receive OTP"
                : "Enter the OTP sent to your email"}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-2">

            {error && (
              <Alert className="bg-red-500/20 border border-red-400">
                <AlertDescription className="text-red-200 text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}

           <form onSubmit={handleSubmit}  className="space-y-4">

              {authStep === "email" && (
                <div className="space-y-1">
                  <span className="flex items-center gap-1">
                    <Label className="text-gray-200">Email</Label>
                    <span className="text-red-400">*</span>
                  </span>

                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isEmailLocked}
                    className={`h-12 bg-white/20 text-white placeholder:text-white border-white/20 ${isEmailLocked ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                  />
                </div>
              )}

              {authStep === "otp" && (
                <div className="space-y-2">
                  <FormField
                    label="Enter OTP"
                    type="text"
                    value={otp}
                    onChange={handleOtpChange}
                    placeholder="6-digit OTP"
                    className="text-center text-lg tracking-widest h-12 bg-white/20 text-white border-white/20 placeholder:text-white"
                  />

                  <div className="flex justify-between text-sm mt-1">
                   <button
  type="button"
  onClick={handleBack}
  className="text-gray-300 hover:text-white flex items-center gap-1"
>         
                      <ArrowLeft size={14} /> Back
                    </button>

                    {otpTimer > 0 ? (
                      <span className="text-yellow-200 font-semibold">
                        Resend in {otpTimer}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={sendOTP}
                        className="text-yellow-300 hover:text-yellow-200 font-semibold"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}

             <Button
  type="submit"
  disabled={isLoading || (authStep === "otp" && otp.length !== 6)}
  className="w-full h-12 bg-green-900 hover:bg-green-800 text-white rounded-xl font-semibold"
>
{isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {authStep === "email" ? "Sending..." : "Verifying..."}
                  </>
                ) : authStep === "email" ? (
                  "Send OTP"
                ) : (
                  "Verify OTP"
                )}
</Button>



              <p className="text-xs text-gray-300 text-center py-2">
                Design and Developed by{" "}
                <a
                  className="font-semibold text-white"
                  href="https://kanishkasoftware.com/"
                  target="_blank"
                >
                  Kanishka Software
                </a>
              </p>
         </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
